import { useId, type InputHTMLAttributes, type ReactNode, type Ref } from 'react';

import { cn } from '@/lib/cn';

import { FieldMessages } from './FieldMessages';
import { useCanEdit } from './row-viewer';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  description?: string;
  error?: string;
  /**
   * A selection column in a 36 px table row, where the ROW is the wide target.
   *
   * DESIGN.md §5 states both halves of this: «44 px-gulvet gjelder knapper og avkryssinger, som
   * ikke har bredde å kompensere med — en tabellrad har hele skjermbredden». A form checkbox has
   * neither height nor width to spare and gets the floor; a row of them down a table already has
   * the width, and forcing 44 px there would cost the 36 px row the same paragraph fixes.
   */
  dense?: boolean;
  ref?: Ref<HTMLInputElement>;
}

/**
 * A consent checkbox is a legal artefact, not decoration: it is never pre-checked, it is always
 * labelled by something the user can read, and the label itself is clickable.
 *
 * NO NOTCH HERE, ON PURPOSE. A checkbox's name is a sentence you agree to, not a two-word field
 * name, and it has to sit beside the box so the thing you are ticking and the thing you are
 * agreeing to are one line of reading. What it DOES share with every other field is the message
 * slot below it — same component, same size, same rules (see `FieldMessages`).
 */
export function Checkbox({
  label,
  description,
  error,
  className,
  id,
  dense = false,
  disabled,
  ...rest
}: CheckboxProps): ReactNode {
  /* Lesemodus (D1): i en skuff leseren ikke kan endre, viser haken verdien og ingenting mer. */
  const editable = useCanEdit();
  const generated = useId();
  const inputId = id ?? generated;
  const descriptionId = `${inputId}-description`;
  const errorId = `${inputId}-error`;
  const describedBy = [description ? descriptionId : null, error ? errorId : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={cn('flex flex-col', className)}>
      {/* This row used to carry `reserves-focus-ring` — four pixels of padding top and bottom so
          a 16px box on an 18px line had something of its own to absorb the ring with. Measured
          before that fix, the ring reached 1.5px into the heading below at 1440px and 3.5px at
          420px.
          The padding is gone because the ring is: since 28 August the focus indicator is drawn
          INSIDE the control, so it reaches nothing and there is nothing to absorb. The row keeps
          the eight pixels it was donating to a halo. */}
      {/*
        THE BOX AND ITS WORDS ARE THE TARGET, NOT THE 16 PX BOX ALONE. The label is `htmlFor`-bound
        and 44 px tall, so it clears DESIGN.md §5's floor without the box itself growing into
        something that looks like a button. `dense` opts a table's selection column out; see the
        prop.

        BUT NOT THE WHOLE ROW (QA-runde 13, punkt 38). The label used to be `flex-1`, so the target
        ran to the right edge of the card — 750 × 44 px in the signup-form settings. A click in
        what looked like empty space, to leave «Maks antall svar», took the tick off «Krever
        godkjenning». `self-start` shrinks the row to the box and the words.
      */}
      <div
        className={cn(
          'flex max-w-full gap-2 self-start',
          dense ? 'items-start' : 'min-h-11 items-center',
        )}
      >
        <input
          id={inputId}
          type="checkbox"
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy.length > 0 ? describedBy : undefined}
          /* `check-box` (globals.css): radius 5, kant i 3:1, aksentfyll og hake i «tekst på
             aksent» når den er krysset av (§16.5, G4). Tegnet av oss og ikke av systemet, fordi
             en native boks verken tar radius eller følger aksenten i mørk modus. */
          className={cn('check-box ring-focus', dense ? 'mt-0.5' : null)}
          {...rest}
          disabled={disabled === true || !editable}
        />
        <label
          htmlFor={inputId}
          className={cn(
            'cursor-pointer text-sm leading-snug text-default',
            /* The whole 44 px height is clickable, the width is the words'. */
            dense ? null : 'flex min-h-11 min-w-0 items-center',
          )}
        >
          {label}
        </label>
      </div>
      <FieldMessages
        descriptionId={descriptionId}
        description={description}
        errorId={errorId}
        error={error}
        // Aligned with the label text rather than with the box: 16px checkbox + 8px gap.
        className="ps-6"
      />
    </div>
  );
}
